import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Loader2 } from 'lucide-react';

const countryCodes = [
  { code: '+1', flag: '\u{1F1FA}\u{1F1F8}' }, { code: '+44', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: '+91', flag: '\u{1F1EE}\u{1F1F3}' }, { code: '+86', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: '+49', flag: '\u{1F1E9}\u{1F1EA}' }, { code: '+33', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: '+61', flag: '\u{1F1E6}\u{1F1FA}' }, { code: '+81', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: '+82', flag: '\u{1F1F0}\u{1F1F7}' }, { code: '+971', flag: '\u{1F1E6}\u{1F1EA}' },
  { code: '+55', flag: '\u{1F1E7}\u{1F1F7}' }, { code: '+52', flag: '\u{1F1F2}\u{1F1FD}' },
];

const ContactFormDialog = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
  const [errors, setErrors] = useState({ email: '', phone: '' });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const handlePhoneChange = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 10) {
      setFormData(prev => ({ ...prev, phone: digits }));
      setErrors(prev => ({ ...prev, phone: digits && digits.length !== 10 ? 'Phone must be 10 digits' : '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };
    if (!validateEmail(formData.email)) { newErrors.email = 'Enter a valid email'; hasErrors = true; }
    if (formData.phone.length !== 10) { newErrors.phone = 'Phone must be 10 digits'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); return; }
    setIsSubmitting(true);
    setTimeout(() => {
      toast({ title: "Request Submitted", description: "Our trademark team will reach out within 24 hours." });
      setIsSubmitting(false);
      setFormData({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
      setErrors({ email: '', phone: '' });
      onClose();
    }, 1500);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[520px] p-0 gap-0 overflow-hidden bg-white rounded-lg" data-testid="contact-form-dialog">
        <div className="p-8">
          <DialogTitle className="sr-only">Contact Form</DialogTitle>
          <div className="mb-6">
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#002FA7]" data-testid="dialog-badge">Limited Offer</p>
            <h2 className="text-2xl font-bold text-[#09090B] mt-2" style={{fontFamily:'Outfit,sans-serif'}} data-testid="dialog-heading">Start Your Trademark Registration</h2>
            <p className="text-sm text-zinc-500 mt-1">Plans from $45. No hidden fees.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-popup-form">
            <div><Label className="text-zinc-700 text-sm font-medium">Name *</Label>
              <Input placeholder="Full name" value={formData.name} onChange={(e) => setFormData(p => ({...p, name: e.target.value}))} required className="mt-1.5 h-10 border-zinc-200 focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20" data-testid="popup-name-input" /></div>

            <div><Label className="text-zinc-700 text-sm font-medium">Email *</Label>
              <Input type="email" placeholder="you@company.com" value={formData.email} onChange={(e) => { setFormData(p => ({...p, email: e.target.value})); setErrors(p => ({...p, email: ''})); }} required className={`mt-1.5 h-10 ${errors.email ? 'border-red-400' : 'border-zinc-200'} focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20`} data-testid="popup-email-input" />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}</div>

            <div><Label className="text-zinc-700 text-sm font-medium">Phone *</Label>
              <div className="flex gap-2 mt-1.5">
                <Select value={formData.countryCode} onValueChange={(v) => setFormData(p => ({...p, countryCode: v}))}>
                  <SelectTrigger className="w-[90px] h-10 border-zinc-200" data-testid="popup-country-code"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[250px]">{countryCodes.map((item) => (<SelectItem key={item.code} value={item.code}><span className="flex items-center gap-1"><span>{item.flag}</span><span className="text-xs">{item.code}</span></span></SelectItem>))}</SelectContent>
                </Select>
                <Input type="tel" placeholder="1234567890" value={formData.phone} onChange={(e) => handlePhoneChange(e.target.value)} required maxLength={10} className={`flex-1 h-10 ${errors.phone ? 'border-red-400' : 'border-zinc-200'} focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20`} data-testid="popup-phone-input" />
              </div>
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}</div>

            <div><Label className="text-zinc-700 text-sm font-medium">What do you need?</Label>
              <Textarea placeholder="e.g. Register my brand name and logo" rows={2} value={formData.description} onChange={(e) => setFormData(p => ({...p, description: e.target.value}))} className="mt-1.5 border-zinc-200 focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 resize-none" data-testid="popup-description-input" /></div>

            <Button type="submit" className="w-full btn-lift bg-[#002FA7] hover:bg-[#001D66] text-white font-semibold h-11 rounded-md" disabled={isSubmitting} data-testid="popup-submit-button">
              {isSubmitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</>) : 'Submit Request'}
            </Button>
            <p className="text-[11px] text-zinc-400 text-center">By submitting, you agree to our Terms and Privacy Policy.</p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormDialog;
